/**
 * Print Rarity chapter — “Why it is yours”.
 * Compact proof page: count hero, 3 stack layers with icons, short meaning
 * cards, icon list rows for focus/steps, and a history insight.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  CalendarDays,
  CheckCircle2,
  Fingerprint,
  Layers,
  Lightbulb,
  Sparkles,
  Target,
} from "lucide-react";
import type {
  MonthlyConsultationBundle,
  RarityFingerprint,
} from "../../../../utils/forecast/monthlyConsultation";
import heroRarity from "../../../../assets/monthly-consultation/pdf/hero-rarity.png";
import rarityStackArt from "../../../../assets/monthly-consultation/pdf/rarity-stack-art.png";
import {
  PdfCard,
  PdfChapterShell,
  PdfInsightStrip,
  PdfPill,
} from "../atoms";
import { clipPdfText } from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardWhite,
  charcoal,
  cream,
  displayFontFamily,
  mutedGray,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF rarity chapter.
 */
export interface PdfChapterRarityProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/** Layer labels for timing stack summary rows. */
const STACK_LAYER_LABELS = ["Decade", "Month", "Signal"] as const;

/** Lucide icons for Decade / Month / Signal cards. */
const STACK_LAYER_ICONS: readonly LucideIcon[] = [
  Layers,
  CalendarDays,
  Sparkles,
];

/**
 * Resolve the webpack URL for the Rarity celestial hero band.
 *
 * @returns Image URL, or empty string when the module did not yield a path
 */
function rarityHeroSrc(): string {
  return typeof heroRarity === "string" ? heroRarity.trim() : "";
}

/**
 * Resolve the webpack URL for the stack timing-rings content art.
 *
 * @returns Image URL, or empty string when the module did not yield a path
 */
function rarityStackArtSrc(): string {
  return typeof rarityStackArt === "string" ? rarityStackArt.trim() : "";
}

/**
 * Keep the first sentence (or soft-clipped text) so cards stay scannable.
 *
 * @param text - Raw paragraph
 * @param maxChars - Soft character cap after sentence trim
 * @returns Short display string
 */
function shortProofLine(text: string, maxChars: number): string {
  const raw = typeof text === "string" ? text.trim() : "";
  if (raw.length === 0) {
    return "";
  }
  const match = raw.match(/^[\s\S]*?[.!?…](?:\s|$)/);
  const sentence =
    match !== null && match[0].trim().length > 0 ? match[0].trim() : raw;
  return clipPdfText(sentence, maxChars);
}

/**
 * Accent color for a stack layer card (Decade / Month / Signal).
 *
 * @param index - Zero-based layer index
 * @returns Theme token hex
 */
function layerAccent(index: number): string {
  if (index === 0) {
    return terracotta;
  }
  if (index === 1) {
    return tanGold;
  }
  return charcoal;
}

/**
 * Format one prior month for a history pill.
 *
 * @param entry - Solar year + lunar month from rarity history
 * @returns Short display label such as `M6 2018`
 */
function formatPriorMonth(entry: {
  solarYear: number;
  lunarMonth: number;
}): string {
  return `M${String(entry.lunarMonth)} ${String(entry.solarYear)}`;
}

/**
 * Parse a stack summary line into a labeled layer row.
 *
 * @param line - Raw stack summary string
 * @param index - Zero-based layer index
 * @returns Display label and value
 */
function parseStackLine(
  line: string,
  index: number
): { label: string; value: string } {
  const safeLine = line.trim();
  const resolvedLabel = STACK_LAYER_LABELS[index];
  const fallbackLabel =
    resolvedLabel !== undefined ? resolvedLabel : `Layer ${String(index + 1)}`;
  const colon = safeLine.indexOf(":");
  if (colon >= 0) {
    const before = safeLine.slice(0, colon).trim();
    const after = safeLine.slice(colon + 1).trim();
    return {
      label: before.length > 0 ? before : fallbackLabel,
      value: after.length > 0 ? after : safeLine,
    };
  }
  return { label: fallbackLabel, value: safeLine };
}

/**
 * Collect trimmed non-empty strings from a rarity array field.
 *
 * @param value - Unknown array candidate
 * @returns Trimmed strings
 */
function stringRows(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/**
 * One icon + short title row for Focus / Action lists.
 *
 * @param props - Marker icon, accent, and clipped title
 * @returns Compact action row
 */
const IconActionRow: React.FC<{
  readonly Icon: LucideIcon;
  readonly accent: string;
  readonly title: string;
}> = ({ Icon, accent, title }) => {
  const safe = typeof title === "string" ? title.trim() : "";
  if (safe.length === 0) {
    return null;
  }
  return (
    <div
      className="mc-pdf-card"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        background: cardWhite,
        border: `1px solid ${border}`,
        borderRadius: 12,
        padding: "10px 12px",
        boxSizing: "border-box",
        fontFamily: bodyFontFamily,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: `${accent}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={14} color={accent} strokeWidth={2} />
      </span>
      <p
        style={{
          margin: 0,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.4,
          color: charcoal,
          minWidth: 0,
        }}
      >
        {safe}
      </p>
    </div>
  );
};

/**
 * Why it is yours — personal stack, history, usual ask, and action steps.
 */
export const PdfChapterRarity: React.FC<PdfChapterRarityProps> = ({
  bundle,
  pageNumber,
}) => {
  const rarity: RarityFingerprint = bundle.rarity;
  const stackSummary = stringRows(rarity.stackSummary);
  const layers = stackSummary.map((line, index) => parseStackLine(line, index));
  const whatItMeans =
    typeof rarity.whatItMeans === "string"
      ? shortProofLine(rarity.whatItMeans, 140)
      : "";
  const whyNotGeneric =
    typeof rarity.whyNotGeneric === "string"
      ? shortProofLine(rarity.whyNotGeneric, 140)
      : "";
  const usualAskLines = stringRows(rarity.usualAskLines);
  const usualAskFallback =
    typeof rarity.usualAsk === "string" ? rarity.usualAsk.trim() : "";
  const actionSteps = stringRows(rarity.actionSteps)
    .slice(0, 3)
    .map((step) => clipPdfText(step, 90));
  const historyCoach =
    typeof rarity.historyCoach === "string"
      ? shortProofLine(rarity.historyCoach, 160)
      : "";
  const priorOccurrences = Array.isArray(rarity.priorOccurrences)
    ? rarity.priorOccurrences.slice(0, 6)
    : [];
  const priorCount =
    typeof rarity.priorCount === "number" && Number.isFinite(rarity.priorCount)
      ? Math.max(0, Math.floor(rarity.priorCount))
      : 0;
  const countLabel = priorCount === 0 ? "First" : `×${String(priorCount)}`;
  const countCaption =
    priorCount === 0
      ? "time this exact mix has shown up"
      : "similar months in your life";
  const askLines = (
    usualAskLines.length > 0
      ? usualAskLines
      : usualAskFallback.length > 0
        ? [usualAskFallback]
        : []
  )
    .slice(0, 3)
    .map((line) => clipPdfText(line, 90));

  const hero = rarityHeroSrc();
  const stackArt = rarityStackArtSrc();

  return (
    <PdfChapterShell
      chapterId="rarity"
      eyebrow=""
      title=""
      hideHeader={true}
      breakBefore="always"
      compact
      pageNumber={pageNumber}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          flex: "1 1 auto",
          minHeight: 0,
          fontFamily: bodyFontFamily,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: `${terracotta}18`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Fingerprint size={20} color={terracotta} strokeWidth={1.8} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: terracotta,
              }}
            >
              + THIS MONTH
            </p>
            <h2
              style={{
                margin: "4px 0 0",
                fontFamily: displayFontFamily,
                fontSize: 24,
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                color: charcoal,
              }}
            >
              Why it is yours
            </h2>
          </div>
        </div>

        {hero.length > 0 ? (
          <img
            src={hero}
            alt=""
            className="mc-pdf-hero-img mc-pdf-hero-img--rarity"
            style={{ flexShrink: 0 }}
          />
        ) : null}

        <PdfCard>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              marginBottom: layers.length > 0 ? 12 : 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: `${terracotta}14`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Sparkles size={20} color={terracotta} strokeWidth={2} />
              </span>
              <div style={{ minWidth: 0 }}>
                <p
                  style={{
                    margin: 0,
                    fontFamily: displayFontFamily,
                    fontSize: 32,
                    fontWeight: 700,
                    lineHeight: 1,
                    letterSpacing: "-0.03em",
                    color: terracotta,
                  }}
                >
                  {countLabel}
                </p>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: 11,
                    lineHeight: 1.35,
                    color: mutedGray,
                  }}
                >
                  {countCaption}
                </p>
              </div>
            </div>
            {priorOccurrences.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  gap: 6,
                  justifyContent: "flex-end",
                  maxWidth: "52%",
                  overflow: "hidden",
                }}
              >
                {priorOccurrences.map((entry) => (
                  <PdfPill
                    key={`${String(entry.solarYear)}-${String(entry.lunarMonth)}`}
                    label={formatPriorMonth(entry)}
                    color={terracotta}
                    bg={`${terracotta}1f`}
                  />
                ))}
              </div>
            ) : null}
          </div>

          {layers.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 8,
              }}
            >
              {layers.slice(0, 3).map((layer, index) => {
                const accent = layerAccent(index);
                const resolvedIcon = STACK_LAYER_ICONS[index];
                const Icon =
                  resolvedIcon !== undefined ? resolvedIcon : Layers;
                return (
                  <div
                    key={`${layer.label}-${layer.value.slice(0, 24)}`}
                    style={{
                      background: cream,
                      border: `1px solid ${border}`,
                      borderTop: `3px solid ${accent}`,
                      borderRadius: 12,
                      padding: "10px 12px",
                      minWidth: 0,
                      textAlign: "center",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        background: `${accent}18`,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 6,
                      }}
                    >
                      <Icon size={15} color={accent} strokeWidth={2} />
                    </span>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: accent,
                      }}
                    >
                      {layer.label}
                    </p>
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.35,
                        color: charcoal,
                      }}
                    >
                      {clipPdfText(layer.value, 48)}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : null}
        </PdfCard>

        {whatItMeans.length > 0 || whyNotGeneric.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              flexShrink: 0,
            }}
          >
            {whatItMeans.length > 0 ? (
              <div
                className="mc-pdf-card"
                style={{
                  background: cardWhite,
                  border: `1px solid ${border}`,
                  borderRadius: 14,
                  padding: "12px 14px",
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: `${terracotta}18`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Lightbulb size={14} color={terracotta} strokeWidth={2} />
                  </span>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: terracotta,
                    }}
                  >
                    What it means
                  </p>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    lineHeight: 1.45,
                    color: charcoal,
                  }}
                >
                  {whatItMeans}
                </p>
              </div>
            ) : null}
            {whyNotGeneric.length > 0 ? (
              <div
                className="mc-pdf-card"
                style={{
                  background: cardWhite,
                  border: `1px solid ${border}`,
                  borderRadius: 14,
                  padding: "12px 14px",
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: `${tanGold}22`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Fingerprint size={14} color={tanGold} strokeWidth={2} />
                  </span>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: tanGold,
                    }}
                  >
                    Why not generic
                  </p>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    lineHeight: 1.45,
                    color: mutedGray,
                  }}
                >
                  {whyNotGeneric}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        {stackArt.length > 0 ? (
          <div
            className="mc-pdf-card"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              background: cream,
              border: `1px solid ${border}`,
              borderRadius: 16,
              padding: "14px 16px",
              boxSizing: "border-box",
              flex: "1 1 auto",
              minHeight: 132,
            }}
          >
            <img
              src={stackArt}
              alt=""
              aria-hidden="true"
              className="mc-pdf-rarity-stack-art"
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: terracotta,
                }}
              >
                Your timing stack
              </p>
              <p
                style={{
                  margin: "6px 0 0",
                  fontFamily: displayFontFamily,
                  fontSize: 15,
                  fontWeight: 700,
                  lineHeight: 1.35,
                  color: charcoal,
                }}
              >
                Decade, month, and signal lock together for this chart only.
              </p>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: mutedGray,
                }}
              >
                That mix is why this month reads as yours — not a generic forecast.
              </p>
            </div>
          </div>
        ) : null}

        {askLines.length > 0 || actionSteps.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              flex: "1 1 auto",
              alignItems: "stretch",
            }}
          >
            {askLines.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: terracotta,
                  }}
                >
                  Focus on
                </p>
                {askLines.map((line, index) => (
                  <IconActionRow
                    key={`${String(index)}-${line.slice(0, 24)}`}
                    Icon={Target}
                    accent={terracotta}
                    title={line}
                  />
                ))}
              </div>
            ) : null}
            {actionSteps.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: tanGold,
                  }}
                >
                  Action steps
                </p>
                {actionSteps.map((step, index) => (
                  <IconActionRow
                    key={`${String(index)}-${step.slice(0, 24)}`}
                    Icon={CheckCircle2}
                    accent={tanGold}
                    title={step}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {historyCoach.length > 0 ? (
          <div style={{ flexShrink: 0 }}>
            <PdfInsightStrip
              label="From your history"
              text={historyCoach}
              variant="softGold"
            />
          </div>
        ) : null}
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterRarity;
